import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import {
  ALL_DATES,
  UNDATED,
  ago,
  dayKey,
  dayLabel,
  fmtClock,
  fmtTime,
} from "../format";
import { navigate, useRoute } from "../route";
import { clearRecent, loadRecent, rememberRecent, type RecentUser } from "../recentUsers";
import { CopyLink } from "./CopyLink";
import type {
  EngagementPage,
  SessionSummary,
  StoredSession,
  UserIdentity,
  MessageDeliveryStatus,
  UserMessage,
  UserSessionState,
} from "../types";

/**
 * Support view: what has this person said, and what is Bean holding for them
 * right now.
 *
 * Answers the two questions that otherwise need a Firestore console and a log
 * dive: "what did they actually write?" and "why is their call stuck?". The two
 * halves come from different stores and mean different things — the messages are
 * the durable record, the session is live, expiring state — so they are labelled
 * rather than merged.
 *
 * Read-only by construction: nothing here writes, and reading a session takes no
 * lock, so opening this during a live call cannot slow that call down.
 */

/** Plain-words labels for a message-log row's kind. */
const LOG_KIND_LABEL: Record<string, string> = {
  onboarding: "onboarding",
  pre_consent: "before consent",
  tier_blocked: "blocked: over limit",
  nudge: "nudge",
  reply: "system reply",
  meeting: "meeting",
  plan: "plan",
  recap: "recap",
  next_session: "next session",
  resource: "resource",
  other: "sent",
};

/** Seconds as "1.6s" / "3m 30s" / "17h 45m" — receipt lags run to hours. */
function lag(v: number | null): string {
  if (v === null) return "";
  if (v < 60) return `${v.toFixed(1)}s`;
  if (v < 3600) return `${Math.floor(v / 60)}m ${Math.round(v % 60)}s`;
  return `${Math.floor(v / 3600)}h ${Math.round((v % 3600) / 60)}m`;
}

/** One chip per bubble: delivered (with Sent → Delivered time), still waiting
 *  for a receipt, or failed. Slow receipts (> 60s) are tinted. */
function DeliveryChips({ deliveries }: { deliveries?: MessageDeliveryStatus[] }) {
  if (!deliveries || deliveries.length === 0) return null;
  return (
    <div className="small">
      {deliveries.map((d) => {
        const slow = (d.sent_to_delivered_s ?? 0) > 60;
        const [cls, text] =
          d.state === "delivered"
            ? [slow ? "badge warn" : "badge ok", `delivered ${lag(d.sent_to_delivered_s)}`]
            : d.state === "failed"
              ? ["badge bad", `failed${d.error_code ? ` ${d.error_code}` : ""}`]
              : d.state === "no_receipt"
                ? ["badge warn", "no receipt"]
                : ["badge", d.latest_status.toLowerCase() || "sent"];
        return (
          <span
            key={d.delivery_id}
            className={cls}
            title={d.delivered_at ? `Delivered ${fmtTime(d.delivered_at)}` : "No DELIVERED receipt yet"}
          >
            {text}
          </span>
        );
      })}
    </div>
  );
}

interface DayBucket {
  key: string;
  label: string;
  messages: UserMessage[];
}

/**
 * Newest day first — the reason to open this view is almost always "what just
 * happened", so the most recent day is the one to land on. WITHIN a day the
 * messages stay chronological: a conversation read bottom-up is unreadable.
 */
function groupByDay(messages: UserMessage[]): DayBucket[] {
  const buckets = new Map<string, UserMessage[]>();
  for (const m of messages) {
    const key = dayKey(m.timestamp);
    const existing = buckets.get(key);
    if (existing) existing.push(m);
    else buckets.set(key, [m]);
  }
  return [...buckets.entries()]
    .map(([key, msgs]) => ({ key, label: dayLabel(key), messages: msgs }))
    .sort((a, b) => {
      // Undated last: it has no place on a timeline, and it is usually empty.
      if (a.key === UNDATED) return 1;
      if (b.key === UNDATED) return -1;
      return b.key.localeCompare(a.key);
    });
}

const MESSAGE_LIMIT = 200;
/** The engine's per-request cap. */
const TRANSLATE_STEP = 45;

interface Translation {
  language: string;
  text: string;
}

/** Timestamp alone can repeat, and the engine trims the text it translates. */
function translationKey(timestamp: string, content: string): string {
  return `${timestamp}|${content.trim()}`;
}

/** Flags worth surfacing as chips — the usual suspects when a call stalls. */
const GATE_FLAGS = [
  "draft_pending",
  "awaiting_enrich",
  "plan_materials_ready",
  "plan_settled",
  "product_intro_active",
  "final_questions_asked",
  "live_ending",
  "slow_thinking_inflight",
  "research_inflight",
  "discovery_wrap_pending",
  "requires_bot",
  "bot_joined",
] as const;

function SessionCard({ label, stored }: { label: string; stored: StoredSession }) {
  if (!stored.exists) {
    return (
      <div className="card">
        <h3>{label}</h3>
        <p className="muted empty">
          {stored.error ? `Unreadable: ${stored.error}` : "No session cached."}
        </p>
        <p className="muted small">
          <code>{stored.key}</code>
        </p>
      </div>
    );
  }

  const s: SessionSummary = stored.session ?? {};
  const active = GATE_FLAGS.filter((f) => s[f] === true);

  return (
    <div className="card">
      <h3>
        {label}{" "}
        {s.session_type && <span className="badge">{s.session_type}</span>}
      </h3>

      <dl className="kv">
        <dt>Key</dt>
        <dd>
          <code className="muted">{stored.key}</code>
        </dd>
        <dt>Revision</dt>
        <dd>
          {stored.revision ?? "—"}
          {/* The revision is how instances agree on who wrote last; if it never
              moves while turns are happening, write-back is broken. */}
          <span className="muted small"> · written {ago(stored.written_at)}</span>
        </dd>
        <dt>Expires in</dt>
        <dd>
          {stored.ttl_seconds != null
            ? `${Math.round(stored.ttl_seconds / 60)}m`
            : "—"}
        </dd>
        <dt>Goal</dt>
        <dd>{s.current_goal_id || "—"}</dd>
        <dt>Meeting</dt>
        <dd>{s.meeting_session_id || "—"}</dd>
        <dt>Turns</dt>
        <dd>
          {s.total_user_turns ?? 0} total
          <span className="muted small">
            {" "}
            · {s.window_messages ?? 0} in the model window
          </span>
        </dd>
        <dt>Last active</dt>
        <dd>{ago(s.last_active)}</dd>
        {s.persona && (
          <>
            <dt>Persona</dt>
            <dd>{s.persona}</dd>
          </>
        )}
        {stored.size_bytes != null && (
          <>
            <dt>Size</dt>
            <dd className="muted">{(stored.size_bytes / 1024).toFixed(1)} KB</dd>
          </>
        )}
      </dl>

      {active.length > 0 && (
        <p className="small">
          {active.map((f) => (
            <span key={f} className="badge" style={{ marginRight: 4 }}>
              {f}
            </span>
          ))}
        </p>
      )}

      {s.lesson && (
        <p className="muted small">
          Lesson “{s.lesson.title || "untitled"}” — segment{" "}
          {(s.lesson.index ?? 0) + 1} of {s.lesson.segments ?? 0} ({s.lesson.phase})
        </p>
      )}

      {s.agenda && s.agenda.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Agenda step</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {s.agenda.map((step, i) => (
                <tr key={`${step.step}-${i}`}>
                  <td>{step.step}</td>
                  <td className={step.status === "done" ? "ok" : "muted"}>
                    {step.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {s.research_requests && Object.keys(s.research_requests).length > 0 && (
        <p className="muted small">
          Research:{" "}
          {Object.entries(s.research_requests)
            .map(([id, r]) => `${id} → ${r.status} (try ${r.attempt})`)
            .join(" · ")}
        </p>
      )}

      {s.fired_steps && s.fired_steps.length > 0 && (
        <p className="muted small">Fired: {s.fired_steps.join(", ")}</p>
      )}
    </div>
  );
}

/** The launch of the current beta window — the default answer to "since when?". */
const DEFAULT_SINCE = "2026-08-10";

/**
 * Who has actually been using Bean since a date, and how much. Aggregated
 * server-side (see EngagementRow for the definitions); this component only
 * renders and lets a row jump into the lookup below, so digging into one
 * person is a click, not a copy-paste.
 */
function EngagementSummary({ busy }: { busy: boolean }) {
  const [since, setSince] = useState(DEFAULT_SINCE);
  const [page, setPage] = useState<EngagementPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .userEngagement(since)
      .then((p) => {
        if (!cancelled) setPage(p);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [since]);

  const rows = page?.items ?? [];

  return (
    <>
      <header className="section-head">
        <h3>
          Active users{" "}
          {page && <span className="count">[{rows.length}]</span>}
        </h3>
        <span className="small">
          since{" "}
          <input
            type="date"
            value={since}
            // A cleared date input yields "" — keep the last real date rather
            // than firing a request the server will reject.
            onChange={(e) => e.target.value && setSince(e.target.value)}
            disabled={loading}
          />
        </span>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {loading && !page ? (
        <p className="muted empty">Counting…</p>
      ) : rows.length === 0 && !error ? (
        <p className="muted empty">
          Nobody has sent a message since {dayLabel(since)}.
        </p>
      ) : (
        rows.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>User id</th>
                  <th>Active days</th>
                  <th>Last active</th>
                  <th>Messages</th>
                  <th>Goals (new / done)</th>
                  <th>Sessions created</th>
                  <th>Started</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.user_id}
                    // Straight into the lookup below — by user_id, not phone,
                    // so a person who has since reset still opens the account
                    // these numbers belong to.
                    onClick={() => {
                      if (!busy) navigate({ tab: "users", q: r.user_id });
                    }}
                    style={{ cursor: "pointer" }}
                    title={`Open ${r.user_id}`}
                  >
                    <td className="nowrap">{r.phone || "—"}</td>
                    <td className="nowrap">
                      <code className="small">{r.user_id}</code>
                    </td>
                    <td>{r.active_days}</td>
                    <td className="nowrap">{dayLabel(r.last_active_day)}</td>
                    <td>{r.messages_sent}</td>
                    <td>
                      {r.goals_created ?? r.active_goals ?? 0}
                      {r.goals_completed != null ? ` / ${r.goals_completed}` : ""}
                    </td>
                    <td>{r.sessions_created}</td>
                    <td>{r.sessions_started ?? "—"}</td>
                    <td>{r.sessions_completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {page && (
        <p className="muted small">
          Active = they sent at least one message (any channel) on or after{" "}
          {dayLabel(page.since)}. Days are Pacific time, so everyone reads the
          same counts. Sessions count only <code>{page.env}</code> ones —
          completed means the post-session evaluation said complete.
        </p>
      )}
    </>
  );
}

export function Users() {
  const route = useRoute();
  const [query, setQuery] = useState(route.q);
  const [identity, setIdentity] = useState<UserIdentity | null>(null);
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [logOk, setLogOk] = useState(true);
  const [session, setSession] = useState<UserSessionState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [day, setDay] = useState<string>(ALL_DATES);
  const [recent, setRecent] = useState<RecentUser[]>(() => loadRecent());

  const days = useMemo(() => groupByDay(messages), [messages]);

  // The URL names who is open, so a shared link lands on them. Keyed on
  // route.q rather than a mount-only effect: pressing back to an earlier
  // lookup has to actually re-run it.
  const loaded = useRef("");
  useEffect(() => {
    if (!route.q || loaded.current === route.q) return;
    loaded.current = route.q;
    setQuery(route.q);
    void load(route.q);
    // `load` is stable (useCallback with no deps) — listing it would re-run
    // this on every render that recreates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.q]);

  // Land on the newest day whenever a different person is loaded. Without this
  // the previous user's selected date sticks and silently filters the new one —
  // which reads as "they have no messages", the exact wrong conclusion.
  useEffect(() => {
    setDay(days.length > 0 ? days[0].key : ALL_DATES);
  }, [days]);

  const shown = useMemo(
    () =>
      day === ALL_DATES
        ? messages
        : (days.find((d) => d.key === day)?.messages ?? []),
    [day, days, messages],
  );

  const viewedUser = useRef("");
  // Translations live here only, and are gone on reload or on opening someone else.
  const [translations, setTranslations] = useState<Record<string, Translation>>({});
  const [translatingDay, setTranslatingDay] = useState("");
  const [dayNotes, setDayNotes] = useState<Record<string, string>>({});

  // A step at a time, so a long day's translations appear as they arrive.
  const translateDay = async () => {
    if (!identity || translatingDay) return;
    const userId = identity.user_id;
    const key = day;
    const stamps = [...new Set(shown.flatMap((m) => (m.timestamp ? [m.timestamp] : [])))];
    setTranslatingDay(key);
    setDayNotes((n) => ({ ...n, [key]: "" }));
    let found = 0;
    let failed = 0;
    try {
      for (let i = 0; i < stamps.length; i += TRANSLATE_STEP) {
        const page = await api.userTranslate(
          userId, stamps.slice(i, i + TRANSLATE_STEP), MESSAGE_LIMIT);
        // They moved on to someone else: never put this person's chat on screen.
        if (viewedUser.current !== userId) return;
        failed += page.failed;
        const fresh = page.items.filter((it) => it.translation);
        found += fresh.length;
        setTranslations((all) => {
          const next = { ...all };
          for (const it of fresh) {
            next[translationKey(it.timestamp, it.content)] = {
              language: it.language, text: it.translation,
            };
          }
          return next;
        });
      }
      setDayNotes((n) => ({
        ...n,
        [key]: failed > 0
          ? `${failed} could not be translated. Click to try again.`
          : found === 0
            ? "Everything on this day is already in English."
            : "",
      }));
    } catch (e) {
      if (viewedUser.current !== userId) return;
      setDayNotes((n) => ({
        ...n,
        [key]: e instanceof Error ? e.message : "Could not translate.",
      }));
    } finally {
      setTranslatingDay("");
    }
  };

  const load = useCallback(async (q: string) => {
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    setError("");
    try {
      const who = await api.lookupUser(term);
      setIdentity(who);
      viewedUser.current = who.user_id;
      // Only successful lookups are remembered — a typo is not a person, and
      // recording it would push a real one off a six-slot list.
      setRecent(rememberRecent(who));
      // Both halves in parallel; a session read failing must not hide the
      // messages, which are the half that always exists.
      const [msgs, sess] = await Promise.allSettled([
        api.userMessages(who.user_id, MESSAGE_LIMIT),
        api.userSession(who.user_id),
      ]);
      setMessages(msgs.status === "fulfilled" ? msgs.value.messages : []);
      setTranslations({});
      setDayNotes({});
      setLogOk(msgs.status !== "fulfilled" || msgs.value.log_ok !== false);
      setSession(sess.status === "fulfilled" ? sess.value : null);
      if (sess.status === "rejected") {
        setError(
          sess.reason instanceof Error
            ? `Session unavailable: ${sess.reason.message}`
            : "Session unavailable.",
        );
      }
    } catch (e) {
      setIdentity(null);
      setMessages([]);
      setSession(null);
      setError(
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <section>
      <header className="section-head">
        <h2>Users</h2>
        {identity && (
          <span>
            <CopyLink route={{ tab: "users", q: query.trim() }} />{" "}
            <button
              className="ghost"
              onClick={() => void load(query)}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </span>
        )}
      </header>

      <p className="muted small">
        A person’s messages and the session Bean currently holds for them.
        Read-only, and every lookup is logged with your account.
      </p>

      <EngagementSummary busy={loading} />

      <h3>Look up one user</h3>
      <form
        className="add-admin"
        onSubmit={(e) => {
          e.preventDefault();
          navigate({ tab: "users", q: query.trim() });
        }}
      >
        <input
          type="search"
          placeholder="Phone number (any format) or user id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        <button type="submit" disabled={loading || !query.trim()}>
          Look up
        </button>
      </form>

      {recent.length > 0 && (
        <p className="muted small">
          Recent:{" "}
          {recent.map((r) => (
            <button
              key={r.user_id}
              // `small`, not `tab`: .tab drops the border, which reads as a tab
              // only inside the .tabs strip. Standalone these need to look like
              // something you press.
              className="small"
              disabled={loading}
              title={`Open ${r.user_id}`}
              onClick={() => {
                // Re-look-up by user_id, not the handle that was typed: it is the
                // identity that was actually resolved, so a person who has since
                // reset still lands on the account this chip refers to. Goes
                // through the URL so the chip is a real navigation.
                setQuery(r.user_id);
                navigate({ tab: "users", q: r.user_id });
              }}
            >
              {r.handle || r.user_id}
            </button>
          ))}
          <button
            className="ghost small"
            onClick={() => setRecent(clearRecent())}
            title="Forget these — they are stored in this browser"
          >
            clear
          </button>
        </p>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {identity && (
        <>
          <dl className="kv">
            <dt>User id</dt>
            <dd>
              <code>{identity.user_id}</code>
            </dd>
            <dt>Handle</dt>
            <dd>{identity.handle || <span className="muted">—</span>}</dd>
            {identity.user_gen != null && identity.user_gen > 0 && (
              <>
                <dt>Generation</dt>
                {/* A reset mints a fresh user_id, so earlier generations hold a
                    different history that this view will not show. */}
                <dd>
                  {identity.user_gen}
                  <span className="muted small"> · has reset before</span>
                </dd>
              </>
            )}
            {identity.consent_status && (
              <>
                <dt>Consent</dt>
                <dd>{identity.consent_status}</dd>
              </>
            )}
          </dl>

          {session && (
            <>
              <p className="muted small">
                Session store: <code>{session.backend}</code>
                {session.backend !== "redis" && (
                  <span className="badge" style={{ marginLeft: 6 }}>
                    not shared — this is only what one instance holds
                  </span>
                )}
                {session.degraded && (
                  <span className="badge" style={{ marginLeft: 6 }}>
                    Redis unreachable — may be stale
                  </span>
                )}
              </p>
              <SessionCard label="Live session" stored={session.sessions.live} />
              <SessionCard label="iMessage session" stored={session.sessions.imessage} />
            </>
          )}

          <h3>Messages</h3>
          {messages.length === 0 ? (
            <p className="muted empty">No messages stored for this user.</p>
          ) : (
            <>
              {/* Newest day first — the reason to open this is usually "what just
                  happened". "All" stays available so the whole thread can still
                  be read in one pass. */}
              <div className="tabs" role="tablist">
                {days.map((d) => (
                  <button
                    key={d.key}
                    role="tab"
                    aria-selected={day === d.key}
                    className={day === d.key ? "tab active" : "tab"}
                    onClick={() => setDay(d.key)}
                  >
                    {d.label} <span className="count">[{d.messages.length}]</span>
                  </button>
                ))}
                <button
                  role="tab"
                  aria-selected={day === ALL_DATES}
                  className={day === ALL_DATES ? "tab active" : "tab"}
                  onClick={() => setDay(ALL_DATES)}
                >
                  All <span className="count">[{messages.length}]</span>
                </button>
              </div>

            <div className="small muted" style={{ margin: "0.5rem 0" }}>
              {day === ALL_DATES || day === UNDATED ? (
                "Pick a day to translate its messages."
              ) : (
                <>
                  <button
                    type="button"
                    onClick={translateDay}
                    disabled={Boolean(translatingDay)}
                  >
                    {translatingDay === day ? "Translating…" : "Translate this day"}
                  </button>{" "}
                  {dayNotes[day]}
                </>
              )}
            </div>

            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>Channel</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((m, i) => (
                    <tr key={`${m.timestamp}-${i}`}>
                      {/* Inside one day the date is noise; across all of them it
                          is the only thing telling the rows apart. */}
                      <td className="nowrap muted small">
                        {day === ALL_DATES ? fmtTime(m.timestamp) : fmtClock(m.timestamp)}
                      </td>
                      <td className={m.role === "user" ? "" : "muted"}>
                        {m.role}
                        {m.source === "message_log" && (
                          <div>
                            <span
                              className="badge"
                              title="Not in the conversation the model reads — taken from the send log"
                            >
                              {LOG_KIND_LABEL[m.message_kind ?? ""] ?? m.message_kind ?? "sent"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="muted small">{m.channel || "—"}</td>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {m.content}
                        {(() => {
                          const tr = translations[
                            translationKey(m.timestamp ?? "", m.content ?? "")];
                          return tr && (
                            <div className="small muted" style={{ marginTop: "0.25rem" }}>
                              <strong>{tr.language} → English:</strong> {tr.text}
                            </div>
                          );
                        })()}
                        {m.media_url && (
                          <div className="small">
                            <a href={m.media_url} target="_blank" rel="noreferrer">
                              [image]
                            </a>
                          </div>
                        )}
                        <DeliveryChips deliveries={m.deliveries} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          <p className="muted small">
            {day === ALL_DATES
              ? `${messages.length} most recent ${messages.length === 1 ? "message" : "messages"}`
              : `${shown.length} of ${messages.length} loaded, on ${dayLabel(day)}`}{" "}
            from the durable record — not the model’s window, which is trimmed to
            the last few turns — plus every text atula sent outside it
            (onboarding, nudges, system replies) and anything the user wrote
            before consenting, labelled. Days are grouped by your local time.
          </p>
          {!logOk && (
            <p className="error small" role="alert">
              The send log could not be read — showing the conversation only, so
              onboarding and nudge texts may be missing.
            </p>
          )}
        </>
      )}
    </section>
  );
}
